import { useState } from 'react';
import { Menu, MenuItems, MenuButton, MenuItem } from '@headlessui/react';
import IconSvg from 'src/assets/icons/IconSvg';
import NumberInputWithMultiplier from '../input/NumberInputWithMultiplier';
import Button from '../button/Button';
import { useViewport } from '../../context/ViewportContext';
import { useCart } from 'src/contexts/cart-context';
import { useWallet, useNotification } from 'src/hooks';
import { useRaffleActions } from 'src/hooks/useRaffleActions';
import { getRaffleDisplayName, getRaffleImage, satsToBtc } from 'src/utils/raffleHelpers';

const ShoppingCartDropdown = () => {
  const { isMobile } = useViewport();
  const { items, removeFromCart, updateQuantity, clearCart, getTotalBtc, getItemCount } = useCart();
  const { isConnected, signMessage } = useWallet();
  const { buyTickets } = useRaffleActions();
  const notify = useNotification();
  const [isPurchasing, setIsPurchasing] = useState(false);

  const totalBtc = getTotalBtc();
  const itemCount = getItemCount();

  const handleCompletePurchase = async () => {
    if (!isConnected || items.length === 0) return;

    setIsPurchasing(true);

    // Single sign for entire cart
    const itemSummary = items
      .map(item => `${item.quantity}x ${getRaffleDisplayName(item.raffle)}`)
      .join(', ');
    const signature = await signMessage(
      `Confirm purchase of ${itemCount} ticket(s): ${itemSummary} on Omega One.`,
    );

    if (!signature) {
      setIsPurchasing(false);
      return;
    }

    // Buy tickets for each cart item sequentially
    let allSuccess = true;
    const failedItems: string[] = [];

    for (const item of items) {
      const success = await buyTickets(item.raffleId, item.quantity);
      if (!success) {
        allSuccess = false;
        failedItems.push(getRaffleDisplayName(item.raffle));
      }
    }

    setIsPurchasing(false);

    if (allSuccess) {
      clearCart();
      notify('All tickets purchased successfully!', 'success');
    } else if (failedItems.length < items.length) {
      // Partial success - remove successful items
      const failedRaffleNames = new Set(failedItems);
      items.forEach(item => {
        if (!failedRaffleNames.has(getRaffleDisplayName(item.raffle))) {
          removeFromCart(item.raffleId);
        }
      });
      notify(`Some purchases failed: ${failedItems.join(', ')}`, 'error');
    } else {
      notify('Failed to complete purchase. Please try again.', 'error');
    }
  };

  return (
    <Menu>
      <MenuButton className="bg-brand-black text-grayscale-400 p-2.5 rounded-lg relative">
        <IconSvg icon={isMobile ? 'shoppingS' : 'shoppingM'} />
        {itemCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-tangerine rounded-full text-[10px] font-bold text-brand-black flex items-center justify-center">
            {itemCount > 9 ? '9+' : itemCount}
          </span>
        )}
      </MenuButton>

      <MenuItems
        transition
        anchor="bottom end"
        className="xl:w-[568px] w-full mt-[19px] xl:!mt-1.5 z-50 h-[calc(100vh-148px)] xl:h-auto"
      >
        <MenuItem>
          <div className="bg-[#1B1B1F] bg-opacity-60 backdrop-blur-sm absolute top-0 left-0 w-full h-full z-[-1] xl:hidden"></div>
        </MenuItem>

        <div className="p-6 rounded-b-lg xl:rounded-t-lg bg-grayscale-900">
          <div className="flex justify-between items-end mb-4">
            <div className="font-medium text-brand-magnolia text-[24px]">My Cart</div>

            {items.length > 0 && (
              <button className="underline text-grayscale-400" onClick={clearCart}>
                Clear All
              </button>
            )}
          </div>

          <div className="mb-8 space-y-2">
            {items.length === 0 ? (
              <div className="py-8 text-center text-grayscale-400">Your cart is empty</div>
            ) : (
              items.map(item => {
                const itemTotalBtc = satsToBtc(item.raffle.ticketPriceSats) * item.quantity;
                return (
                  <div
                    key={item.raffleId}
                    className="flex flex-col gap-y-3 justify-between items-center p-3 rounded-lg xl:flex-row bg-grayscale-800"
                  >
                    <div className="flex justify-between items-start xl:w-[273px] w-full">
                      <div className="flex gap-2 items-center">
                        <img
                          src={getRaffleImage(item.raffle)}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover"
                        />

                        <div className="space-y-1">
                          <div className="text-lg text-grayscale-200">
                            {getRaffleDisplayName(item.raffle)}
                          </div>

                          <div className="flex gap-2 items-center text-sm text-grayscale-400">
                            <span>Total:</span>

                            <div className="flex gap-1 items-center">
                              <IconSvg icon="bitcoinS" />
                              <span>{itemTotalBtc.toFixed(4)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button className="xl:hidden" onClick={() => removeFromCart(item.raffleId)}>
                        <IconSvg icon="closeS" className="text-grayscale-400" />
                      </button>
                    </div>

                    <NumberInputWithMultiplier
                      size="md"
                      className="xl:w-[144px] w-full"
                      value={item.quantity}
                      onChange={(val: number) => updateQuantity(item.raffleId, val)}
                      min={1}
                      max={item.raffle.supply - item.raffle.ticketsSold}
                    />

                    <button
                      className="hidden xl:block"
                      onClick={() => removeFromCart(item.raffleId)}
                    >
                      <IconSvg icon="closeS" className="text-grayscale-400" />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {items.length > 0 && (
            <>
              <MenuItem as="div">
                <Button
                  variant="primary"
                  size="sm"
                  className="mb-2 w-full"
                  onClick={handleCompletePurchase}
                  disabled={isPurchasing || !isConnected}
                >
                  {isPurchasing
                    ? 'Processing...'
                    : `Complete Purchase • ${totalBtc.toFixed(4)} BTC`}
                </Button>
              </MenuItem>

              {!isConnected && (
                <div className="text-center text-system-error text-sm mb-2">
                  Connect your wallet to complete purchase
                </div>
              )}
            </>
          )}
        </div>
      </MenuItems>
    </Menu>
  );
};

export default ShoppingCartDropdown;
